/**
 * 后台 - 视频渠道列表
 *
 * 提供创建 / 编辑 / 删除 / 复制 apiKey 的入口，依赖父组件传入数据并通过
 * onRefresh 刷新。
 */

import { useEffect, useState } from 'react';
import { Plus, Settings, Trash2, Copy, Files, Loader2, ListX } from 'lucide-react';
import {
  videoChannelAdminApi,
  type VideoChannelAdminView,
} from '../services/videoAdminApi';
import VideoChannelForm from './VideoChannelForm';
import OrphanChannelCleanupModal from './OrphanChannelCleanupModal';
import toast from '../../../components/Toast';

export default function VideoChannelsTab() {
  const [channels, setChannels] = useState<VideoChannelAdminView[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingChannel, setEditingChannel] = useState<VideoChannelAdminView | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCleanup, setShowCleanup] = useState(false);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await videoChannelAdminApi.list();
      setChannels(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载渠道失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除这个视频渠道吗？')) return;
    try {
      await videoChannelAdminApi.remove(id);
      await load();
      toast.success('已删除');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleCopyKey = async (id: string) => {
    setCopyingId(id);
    try {
      const { apiKey } = await videoChannelAdminApi.getKey(id);
      await navigator.clipboard.writeText(apiKey);
      toast.success('API Key 已复制');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '复制失败');
    } finally {
      setCopyingId(null);
    }
  };

  const handleEdit = (channel: VideoChannelAdminView) => {
    setEditingChannel(channel);
    setShowForm(true);
  };

  const handleDuplicate = async (id: string) => {
    setDuplicatingId(id);
    try {
      const created = await videoChannelAdminApi.duplicate(id);
      // 重新加载列表，并优先用最新列表里的同 id 数据填表（字段更全）
      const fresh = await videoChannelAdminApi.list();
      setChannels(fresh);
      const target = fresh.find((x) => x.id === created.id) ?? created;
      setEditingChannel(target);
      setShowForm(true);
      toast.success('已复制，可继续编辑');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '复制失败');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleCreate = () => {
    setEditingChannel(null);
    setShowForm(true);
  };

  const handleFormClose = (changed: boolean) => {
    setShowForm(false);
    setEditingChannel(null);
    if (changed) load();
  };

  const handleCleanupClose = (changed: boolean) => {
    setShowCleanup(false);
    if (changed) load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">视频 API 渠道</h2>
          <p className="text-sm text-light-muted mt-1">配置 Grok / SeedDance 等视频上游</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCleanup(true)}
            className="btn-secondary flex items-center justify-center gap-2 sm:w-auto"
          >
            <ListX className="w-4 h-4" />
            清理未引用渠道
          </button>
          <button onClick={handleCreate} className="btn-primary flex items-center justify-center gap-2 sm:w-auto">
            <Plus className="w-4 h-4" />
            新增渠道
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-light-muted">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          加载中
        </div>
      ) : channels.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          还未配置视频渠道，点击右上角「新增渠道」开始
        </div>
      ) : (
        <div className="bg-white border border-light-border rounded-2xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-light-border text-sm">
              <thead className="bg-light-hover">
                <tr className="text-left text-xs uppercase tracking-wider text-light-muted">
                  <th className="px-4 py-3">名称</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">API URL</th>
                  <th className="px-4 py-3">API Key</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {channels.map((c) => (
                  <tr key={c.id} className="hover:bg-light-hover/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-light-text">{c.name}</div>
                      {c.remark && <div className="text-xs text-light-muted mt-0.5">{c.remark}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1.5">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-light-hover">
                          {c.providerLabel}
                        </span>
                        <div className="text-xs text-light-muted">
                          <span className="mr-1">上游模型 ID</span>
                          <span
                            className="inline-flex max-w-[240px] items-center rounded-md bg-violet-50 px-2 py-0.5 font-mono text-[11px] text-violet-700 ring-1 ring-violet-200 break-all"
                            title={c.apiModelId}
                          >
                            {c.apiModelId}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-light-secondary truncate max-w-[280px]" title={c.apiUrl}>
                      {c.apiUrl}
                    </td>
                    <td className="px-4 py-3 text-light-secondary font-mono text-xs">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{c.apiKeyMasked}</span>
                        <button
                          onClick={() => handleCopyKey(c.id)}
                          className="p-1 rounded hover:bg-light-hover text-light-secondary"
                          title="复制 API Key"
                          disabled={copyingId === c.id}
                        >
                          {copyingId === c.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill isActive={c.isActive} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChannelActions
                        channel={c}
                        copyingId={copyingId}
                        duplicatingId={duplicatingId}
                        onCopyKey={handleCopyKey}
                        onDuplicate={handleDuplicate}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {channels.map((c) => (
              <div key={c.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-light-text break-all">{c.name}</div>
                    {c.remark && <div className="mt-1 text-xs text-light-muted">{c.remark}</div>}
                  </div>
                  <StatusPill isActive={c.isActive} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-light-hover">
                    {c.providerLabel}
                  </span>
                  <code className="rounded-md bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700 break-all">
                    {c.apiModelId}
                  </code>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoBlock label="API URL" value={c.apiUrl} mono />
                  <InfoBlock label="API Key" value={c.apiKeyMasked} mono />
                </div>
                <ChannelActions
                  channel={c}
                  copyingId={copyingId}
                  duplicatingId={duplicatingId}
                  onCopyKey={handleCopyKey}
                  onDuplicate={handleDuplicate}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  mobile
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <VideoChannelForm channel={editingChannel} onClose={handleFormClose} />
      )}

      {showCleanup && (
        <OrphanChannelCleanupModal onClose={handleCleanupClose} />
      )}
    </div>
  );
}

function StatusPill({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
      }`}
    >
      {isActive ? '启用' : '禁用'}
    </span>
  );
}

function InfoBlock({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className={`mt-1 text-sm text-light-text break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function ChannelActions({
  channel,
  copyingId,
  duplicatingId,
  onCopyKey,
  onDuplicate,
  onEdit,
  onDelete,
  mobile = false,
}: {
  channel: VideoChannelAdminView;
  copyingId: string | null;
  duplicatingId: string | null;
  onCopyKey: (id: string) => void;
  onDuplicate: (id: string) => void;
  onEdit: (channel: VideoChannelAdminView) => void;
  onDelete: (id: string) => void;
  mobile?: boolean;
}) {
  const containerClass = mobile
    ? 'flex flex-wrap items-center gap-2'
    : 'inline-flex items-center gap-1.5';
  const baseButtonClass = mobile
    ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
    : 'p-1.5 rounded-lg hover:bg-light-hover text-light-secondary';

  return (
    <div className={containerClass}>
      <button
        onClick={() => onCopyKey(channel.id)}
        className={baseButtonClass}
        title="复制 API Key"
        disabled={copyingId === channel.id}
      >
        {copyingId === channel.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
        {mobile && '复制 Key'}
      </button>
      <button
        onClick={() => onDuplicate(channel.id)}
        className={baseButtonClass}
        title="复制渠道配置"
        disabled={duplicatingId === channel.id}
      >
        {duplicatingId === channel.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Files className="w-4 h-4" />
        )}
        {mobile && '复制'}
      </button>
      <button
        onClick={() => onEdit(channel)}
        className={baseButtonClass}
        title="编辑"
      >
        <Settings className="w-4 h-4" />
        {mobile && '编辑'}
      </button>
      <button
        onClick={() => onDelete(channel.id)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs text-rose-500 hover:bg-rose-50'
          : 'p-1.5 rounded-lg hover:bg-rose-50 text-rose-500'}
        title="删除"
      >
        <Trash2 className="w-4 h-4" />
        {mobile && '删除'}
      </button>
    </div>
  );
}
